/**
 * CommandCenter — simplified: prioritized by urgency, top 2 visible,
 * rest tucked behind "Show N more opportunities".
 *
 * Status is DERIVED from live sim state (not from a click). Clicking a
 * CTA either applies a measurable simulation effect (e.g. transfer),
 * routes the user to the right follow-up tab, or merely acknowledges
 * an informational insight. The badge honestly reflects which of
 * those happened.
 */
import React, { useMemo, useState } from 'react';
import { Card } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import {
    AlertTriangle, Zap, ArrowLeftRight, Lightbulb, Sparkles, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    useDistributionDemo,
    computeInsightStatus,
} from '../DistributionDemoContext';
import InsightStatusBadge from './InsightStatusBadge';

const CATEGORY_META = {
    'Supply gap':         { icon: AlertTriangle, tone: 'rose' },
    'Channel imbalance':  { icon: ArrowLeftRight, tone: 'amber' },
    'Stock transfer':     { icon: Zap, tone: 'cyan' },
    'Attach opportunity': { icon: Lightbulb, tone: 'indigo' },
};

const TONE_BAR = {
    rose:   'bg-rose-500',
    amber:  'bg-amber-500',
    cyan:   'bg-cyan-500',
    indigo: 'bg-indigo-500',
};

const SEVERITY_RANK = { critical: 0, warning: 1, opportunity: 2 };

const formatEur = (n) => `€${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function CommandCenter({ insights = [], projections = [], hasOptionsBySku = {}, onExploreGap }) {
    const [showMore, setShowMore] = useState(false);
    const {
        acknowledgedInsights, acknowledgeInsight,
        setWarehouseTransfer, warehouseTransfer, leadTimeOverride,
        selectedStrategy, approvedReplenishments,
        flashNodes,
    } = useDistributionDemo();

    const projectionBySku = Object.fromEntries(projections.map(p => [p.sku, p]));

    const prioritized = useMemo(() => {
        return [...insights].sort((a, b) => (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9));
    }, [insights]);

    const PRIMARY_LIMIT = 2;
    const primary = prioritized.slice(0, PRIMARY_LIMIT);
    const secondary = prioritized.slice(PRIMARY_LIMIT);

    const statusFor = (insight) => {
        const p = projectionBySku[insight.sku];
        return computeInsightStatus(insight, {
            projection: p,
            acknowledged: !!acknowledgedInsights[insight.insight_id],
            warehouseTransfer,
            leadTimeOverride,
            selectedStrategyForSku: Object.entries(selectedStrategy).some(([sid]) => sid.includes(insight.sku)),
            approvedForSku: Object.entries(approvedReplenishments).some(([sid, v]) => v && sid.includes(insight.sku)),
            hasOptions: !!hasOptionsBySku[insight.sku] || !!insight.transfer_qty,
        });
    };

    const handlePrimaryClick = (insight) => {
        flashNodes(insight.affects_nodes || []);
        if (insight.impact_type === 'transfer_opportunity' && (!warehouseTransfer || warehouseTransfer.sku !== insight.sku)) {
            setWarehouseTransfer({
                sku: insight.sku,
                qty: insight.transfer_qty,
                from: insight.transfer_from,
                to: insight.transfer_to,
            });
            toast.success(`Transfer applied — simulating ${insight.transfer_qty} units Oslo → Sweden`);
        } else if (insight.impact_type === 'revenue_at_risk') {
            toast.success('Opening Compare options in Orders & Automation');
            if (onExploreGap) onExploreGap(insight.sku);
        } else {
            // Channel rebalance / attach opportunity — no measurable sim effect.
            acknowledgeInsight(insight.insight_id);
            toast.success('Acknowledged — Owntune will apply this on the next planning cycle');
        }
    };

    const impactLine = (insight) => {
        const p = projectionBySku[insight.sku];
        switch (insight.impact_type) {
            case 'revenue_at_risk':
                if (!p) return 'Live projection unavailable';
                if (p.supplyGapDays <= 0) return 'Gap closed by current simulation';
                return `${p.supplyGapDays}-day gap · ${formatEur(p.revenueAtRisk)} at risk`;
            case 'channel_rebalance':
                return `~${insight.potential_units_shifted} units could be rebalanced`;
            case 'transfer_opportunity':
                return `${insight.transfer_qty} units · 2-day ETA · €${insight.transfer_cost_eur.toFixed(2)}/unit`;
            case 'attach_opportunity':
                return `${Math.round(insight.assumed_attach_rate * 100)}% assumed attach uplift`;
            default:
                return '';
        }
    };

    const primaryCtaLabel = (insight) => {
        const status = statusFor(insight);
        if (insight.impact_type === 'revenue_at_risk') return 'Compare options';
        if (insight.impact_type === 'transfer_opportunity') {
            if (warehouseTransfer && warehouseTransfer.sku === insight.sku) return 'Transfer applied';
            return 'Apply transfer';
        }
        if (status === 'acknowledged') return 'Acknowledged';
        return insight.action_cta || 'Acknowledge';
    };

    const primaryCtaDisabled = (insight) => {
        const status = statusFor(insight);
        if (insight.impact_type === 'transfer_opportunity' && warehouseTransfer && warehouseTransfer.sku === insight.sku) return true;
        if (status === 'acknowledged') return true;
        return false;
    };

    const InsightRow = ({ insight, dense }) => {
        const meta = CATEGORY_META[insight.category] || CATEGORY_META['Supply gap'];
        const Icon = meta.icon;
        const bar = TONE_BAR[meta.tone] || TONE_BAR.rose;
        const status = statusFor(insight);
        return (
            <Card
                className={`relative overflow-hidden border-slate-200 ${dense ? '' : ''}`}
                data-testid={`insight-card-${insight.insight_id}`}
            >
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${bar}`} />
                <div className={`${dense ? 'p-3' : 'p-4'} pl-4`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className="h-8 w-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                                <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                                        {insight.category}
                                    </span>
                                    <InsightStatusBadge
                                        status={status}
                                        testid={`insight-status-${insight.insight_id}`}
                                    />
                                </div>
                                <div className="font-semibold text-slate-900 leading-snug text-sm mt-0.5">
                                    {insight.headline}
                                </div>
                                <div className="text-xs text-slate-600 mt-1">
                                    {impactLine(insight)}
                                </div>
                                {!dense && (
                                    <div className="text-xs text-slate-500 mt-1">
                                        {insight.recommended_action}
                                    </div>
                                )}
                            </div>
                        </div>
                        <Button
                            size="sm"
                            variant={primaryCtaDisabled(insight) ? 'outline' : 'default'}
                            onClick={() => handlePrimaryClick(insight)}
                            disabled={primaryCtaDisabled(insight)}
                            data-testid={`insight-cta-${insight.insight_id}`}
                            className="flex-shrink-0"
                        >
                            {primaryCtaLabel(insight)}
                        </Button>
                    </div>
                </div>
            </Card>
        );
    };

    return (
        <section data-testid="command-center">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-indigo-500" />
                    <h3 className="text-base font-semibold text-slate-900">Priority insights</h3>
                </div>
                <p className="text-xs text-slate-500">
                    Ordered by urgency · live from the current scenario
                </p>
            </div>

            <div className="space-y-2">
                {primary.map(ins => <InsightRow key={ins.insight_id} insight={ins} />)}
            </div>

            {secondary.length > 0 && (
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={() => setShowMore(v => !v)}
                        className="w-full flex items-center justify-between text-xs text-slate-500 hover:text-slate-900 py-2"
                        data-testid="insights-show-more"
                        aria-expanded={showMore}
                    >
                        <span>{showMore ? 'Hide' : 'Show'} {secondary.length} more opportunit{secondary.length === 1 ? 'y' : 'ies'}</span>
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`} />
                    </button>
                    {showMore && (
                        <div className="space-y-2">
                            {secondary.map(ins => <InsightRow key={ins.insight_id} insight={ins} dense />)}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
